import { QuestionnaireCategoryItemsItemOneOf } from '@/api/generated-catch-backend/model';
import { RefObject, useEffect } from 'react';
import { QuestionHeaderSkeleton, QuestionSkeleton } from './question-skeleton';
import { ActivityNumberStepperQuestion } from './questionTypes/activity-number-stepper-question';
import { QuestionHeader } from './questionTypes/components/question-header';
import { DateQuestion } from './questionTypes/date-question';
import { DecimalQuestion } from './questionTypes/decimal-question';
import { HeightWeightQuestion } from './questionTypes/height-weight-question';
import { MultiselectQuestion } from './questionTypes/multiselect-question';
import { NumberQuestion } from './questionTypes/number-question';
import { NumberStepperQuestion } from './questionTypes/number-stepper-question';
import { SelectQuestion } from './questionTypes/select-question';
import { TextQuestion } from './questionTypes/text-question';
import { HeightWeightQuestion as HeightWeightQuestionType } from './types';
import { NameEmailQuestion } from './questionTypes/name-email-question';
import { EmailOnlyQuestion } from './questionTypes/email-only-question';
import { SkipDisplay } from '../../../skip-display/skip-display';

// Extended question type that includes our custom activity matrix question
type QuestionType = QuestionnaireCategoryItemsItemOneOf;

interface QuestionComponentProps {
  question: QuestionType;
  isLoading?: boolean;
  hideWhyWeAsk?: boolean;
  onWhyWeAskExpand?: () => void;
  questionTitleRef?: RefObject<HTMLDivElement>;
  onNext?: (args: { skipQuestion: boolean }) => void;
  onSkip?: () => void;
}

export const Question = ({ question, isLoading, hideWhyWeAsk, onWhyWeAskExpand, questionTitleRef, onNext, onSkip }: QuestionComponentProps) => {
  useEffect(() => {
    if (questionTitleRef?.current) {
      questionTitleRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [questionTitleRef]);
  // Special handling for full-screen questions
  if (question.questionType === 'email-only') {
    return <EmailOnlyQuestion question={question} />;
  }

  const renderQuestionComponent = () => {
    switch (question.questionType) {
      case 'text':
        return <TextQuestion question={question} />;

      case 'number':
        return <NumberQuestion question={question} />;

      case 'decimal':
        return <DecimalQuestion question={question} />;

      case 'date':
        return <DateQuestion question={question} />;

      case 'select':
        return <SelectQuestion question={question} onAutoAdvance={onNext} />;

      case 'radio':
        return <SelectQuestion question={question} onAutoAdvance={onNext} />;

      case 'multiselect':
        return <MultiselectQuestion question={question} onAutoAdvance={onNext} />;

      case 'checkbox':
        return <MultiselectQuestion question={question} onAutoAdvance={onNext} />;

      case 'height':
        return <HeightWeightQuestion question={question} />;

      case 'weight':
        return <HeightWeightQuestion question={question} />;

      case 'number-stepper':
        return <NumberStepperQuestion question={question} />;

      case 'height-weight':
        return <HeightWeightQuestion question={question as HeightWeightQuestionType} />;

      case 'exercise-activity-matrix':
        return <ActivityNumberStepperQuestion question={question} />;

      case 'name-email':
        return <NameEmailQuestion question={question} />;

      default:
        console.warn(`Unknown question type: ${(question as QuestionType).questionType}`);
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="questionnaire-question-layout pointer-events-none">
        <QuestionHeaderSkeleton />
        <div className="questionnaire-question-form">
          <QuestionSkeleton
            skeleton={{
              questionType: question.questionType,
              optionCount: 'options' in question && question.options ? question.options.length : undefined,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="questionnaire-question-layout">
      <QuestionHeader
        question={question}
        hideWhyWeAsk={hideWhyWeAsk || false}
        onWhyWeAskExpand={onWhyWeAskExpand || (() => ({}))}
        questionTitleRef={questionTitleRef}
      />
      <div className="questionnaire-question-form pb-[185px]">
        {renderQuestionComponent()}
        {onSkip && <SkipDisplay onSkip={onSkip} />}
      </div>
    </div>
  );
};
